import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, catchError, map, throwError } from 'rxjs';
import { AppErrors } from '@fitness-force/models';
import {
  CommissionPlanRuleResponse,
  CreateCommissionPlanRuleRequest,
  UpdateCommissionPlanRuleRequest,
  ListCommissionPlanRulesResponse,
  ListCommissionPlanRulesRequest,
  CreateResponseData,
  UpdateResponseData
} from '../models';
import { environment } from '@fitness-force/environments';
import { CONST_URL_SEGEMENTS } from '@fitness-force/constants';

@Injectable({
  providedIn: 'root'
})
export class CommissionPlanRuleService {
  private http = inject(HttpClient);

  private readonly API_BASE_URL =  environment.APP_URL;

  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json'
    })
  };

  private buildParams(params: Record<string, unknown>): HttpParams {
    let httpParams = new HttpParams();
    
    Object.keys(params).forEach(key => {
      const value = params[key];
      if (value !== null && value !== undefined) {
        httpParams = httpParams.set(key, value.toString());
      }
    });
    
    return httpParams;
  }

  // Get all rules for a commission plan
  getCommissionPlanRules(planId: number, request?: ListCommissionPlanRulesRequest): Observable<ListCommissionPlanRulesResponse> {
    const params: Record<string, unknown> = {
      PageNumber: request?.page || 1,
      PageSize: request?.page_size || 10
    };
    
    if (request?.active_only !== undefined) {
      params['ActiveOnly'] = request.active_only;
    }
    
    return this.http.get<ListCommissionPlanRulesResponse>(
      this.API_BASE_URL + CONST_URL_SEGEMENTS.COMMISSION.GET_COMPANY_PLAN_RULES
        .replace('{plan_id}', planId.toString()),
      {
        headers: this.httpOptions.headers,
        params: this.buildParams(params)
      }
    ).pipe(
      map((res) => res),
      catchError((error) => {
        return throwError(() => new AppErrors(error));
      })
    );
  }

  // Get a specific rule by ID
  getCommissionPlanRule(planId: number, ruleId: number): Observable<CommissionPlanRuleResponse> {
    return this.http.get<CommissionPlanRuleResponse>(
      this.API_BASE_URL + CONST_URL_SEGEMENTS.COMMISSION.GET_COMPANY_PLAN_RULE_BY_ID
        .replace('{plan_id}', planId.toString())
        .replace('{rule_id}', ruleId.toString()),
      {
        headers: this.httpOptions.headers
      }
    ).pipe(
      map((res) => res),
      catchError((error) => {
        return throwError(() => new AppErrors(error));
      })
    );
  }

  // Create a new rule
  createCommissionPlanRule(planId: number, request: CreateCommissionPlanRuleRequest): Observable<CreateResponseData> {
    return this.http.post<CreateResponseData>(
      this.API_BASE_URL + CONST_URL_SEGEMENTS.COMMISSION.CREATE_COMPANY_PLAN_RULE
        .replace('{plan_id}', planId.toString()),
      request,
      {
        headers: this.httpOptions.headers
      }
    ).pipe(
      map((res) => res),
      catchError((error) => {
        return throwError(() => new AppErrors(error));
      })
    );
  }

  // Update an existing rule
  updateCommissionPlanRule(planId: number, ruleId: number, request: UpdateCommissionPlanRuleRequest): Observable<UpdateResponseData> {
    return this.http.put<UpdateResponseData>(
      this.API_BASE_URL + CONST_URL_SEGEMENTS.COMMISSION.UPDATE_COMPANY_PLAN_RULE
        .replace('{plan_id}', planId.toString())
        .replace('{rule_id}', ruleId.toString()),
      request,
      {
        headers: this.httpOptions.headers
      }
    ).pipe(
      map((res) => res),
      catchError((error) => {
        return throwError(() => new AppErrors(error));
      })
    );
  }

  // Deactivate a rule
  deactivateCommissionPlanRule(planId: number, ruleId: number): Observable<void> {
    return this.http.delete<void>(
      this.API_BASE_URL + CONST_URL_SEGEMENTS.COMMISSION.DELETE_COMPANY_PLAN_RULE
        .replace('{plan_id}', planId.toString())
        .replace('{rule_id}', ruleId.toString()),
      {
        headers: this.httpOptions.headers
      }
    ).pipe(
      map((res) => res),
      catchError((error) => {
        return throwError(() => new AppErrors(error));
      })
    );
  }

  // Get active rules only
  getActiveCommissionPlanRules(planId: number): Observable<ListCommissionPlanRulesResponse> {
    return this.http.get<ListCommissionPlanRulesResponse>(this.API_BASE_URL + CONST_URL_SEGEMENTS.COMPANIES.GET_ACTIVE_COMMISSION_PLAN_RULES
      .replace('{plan_id}', planId.toString()), {
      headers: this.httpOptions.headers
    }).pipe(
      map((res) => res),
      catchError((error) => {
        return throwError(() => new AppErrors(error));
      })
    );
  }
}

